import { getPairColor } from "@trader-dashboard/shared";

type PairSelectorProps = {
  pairs: string[];
  hiddenPairs: string[];
  onTogglePair: (symbol: string) => void;
  onShowAll: () => void;
  onHideAll: () => void;
};

/**
 * Legend-as-control: each chip is both the pair's color key and its
 * visibility toggle. Hidden pairs stay in place, dimmed, so the set of
 * available pairs never shifts under the cursor.
 */
export const PairSelector = ({
  pairs,
  hiddenPairs,
  onTogglePair,
  onShowAll,
  onHideAll,
}: PairSelectorProps): React.ReactNode => {
  if (pairs.length === 0) return null;

  const allHidden = hiddenPairs.length === pairs.length;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {pairs.map((symbol) => {
        const hidden = hiddenPairs.includes(symbol);
        return (
          <button
            key={symbol}
            type="button"
            onClick={() => onTogglePair(symbol)}
            aria-pressed={!hidden}
            title={hidden ? `Show ${symbol}` : `Hide ${symbol}`}
            className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
              hidden
                ? "border-border bg-muted/40 text-muted-foreground"
                : "border-border bg-background text-foreground hover:bg-muted/50"
            }`}
          >
            <span
              className="h-1 w-4 rounded-full"
              style={{ backgroundColor: hidden ? "#d1d5db" : getPairColor(symbol) }}
            />
            {symbol}
          </button>
        );
      })}
      <button
        type="button"
        onClick={allHidden ? onShowAll : onHideAll}
        className="rounded-md px-2 py-1.5 text-xs text-muted-foreground underline-offset-4 hover:underline"
      >
        {allHidden ? "Show all" : "Hide all"}
      </button>
    </div>
  );
};
